import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db, punchEntriesRef } from "../../lib/firebase";
import {
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import PrintableTimecard from "../../components/timecards/PrintableTimecard";

const FALLBACK_TIMECARD_CONFIG = {
  department: "80001",
  accountNumber: "670500",
  supervisorName: "Patrick Gauthier",
  supervisorUid: "AzDsDKAgT8cpqJSgJ3iBBt6AKmd2",
};

/* ---------- DATE HELPERS ---------- */
function startOfWeekMonday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function endOfWeekSunday(d) {
  const mon = startOfWeekMonday(d);
  const sun = addDays(mon, 6);
  sun.setHours(23, 59, 59, 999);
  return sun;
}

function toDate(ts) {
  if (!ts) return null;
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function fmtDay(d) {
  return d.toLocaleDateString(undefined, { weekday: "long" });
}

function fmtTime(d) {
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function hoursBetween(start, end) {
  if (!start || !end) return 0;
  return Math.max(0, (end.getTime() - start.getTime()) / 3600000);
}

function getSignatureUrl(user) {
  return (
    user?.signatureURL ||
    user?.signatureUrl ||
    user?.signatureImageURL ||
    user?.signatureImageUrl ||
    user?.signaturePngURL ||
    user?.signaturePngUrl ||
    ""
  );
}

function buildFullAddress(user = {}) {
  const street =
    user.street ||
    user.streetAddress ||
    user.address1 ||
    user.address ||
    "";

  const cityState = [user.city, user.state].filter(Boolean).join(", ");

  return [street, user.address2, cityState, user.zip]
    .filter(Boolean)
    .join(" ")
    .trim();
}

function normalizeKey(v) {
  return String(v || "").trim().toLowerCase();
}

function readMoney(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function getEntryPayRate(entry, fallbackRate) {
  const raw =
    entry.payRate ??
    entry.rate ??
    entry.hourlyRate ??
    entry.pay?.rate ??
    fallbackRate;

  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function buildRows(entryDocs, fallbackPayRate, providerLevel) {
  return entryDocs
    .map((p) => {
      const start = toDate(p.startTime);
      const end = toDate(p.endTime);

      if (!start || !end) return null;
      if (p.deleted) return null;
      if (p.open) return null;

      const hours =
        typeof p.hoursWorked === "number"
          ? Number(p.hoursWorked.toFixed(2))
          : Number(hoursBetween(start, end).toFixed(2));

      const payRate =
        typeof p.rateUsed === "number"
          ? Number(p.rateUsed.toFixed(2))
          : getEntryPayRate(p, fallbackPayRate);

      const pay =
        typeof p.totalPay === "number"
          ? Number(p.totalPay.toFixed(2))
          : typeof payRate === "number"
          ? Number((hours * payRate).toFixed(2))
          : null;

      return {
        date: start,
        day: fmtDay(start),
        timeIn: fmtTime(start),
        timeOut: fmtTime(end),
        hours,
        payRate,
        pay,
        providerLevel,
      };
    })
    .filter(Boolean);
}

export default function Timecard() {
  const uid = auth.currentUser?.uid;
  const printRef = useRef(null);

  const [weekStart, setWeekStart] = useState(startOfWeekMonday(new Date()));
  const weekEnd = useMemo(() => endOfWeekSunday(weekStart), [weekStart]);

  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ totalHours: 0, totalPay: 0 });
  const [supervisorSignature, setSupervisorSignature] = useState("");
  const [timecardConfig, setTimecardConfig] = useState(FALLBACK_TIMECARD_CONFIG);

  useEffect(() => {
    if (!uid) return;

    async function load() {
      const userSnap = await getDoc(doc(db, "users", uid));
      const userData = userSnap.exists() ? userSnap.data() : {};

      const cfgSnap = await getDoc(doc(db, "appConfig", "timecards"));
      const cfg = cfgSnap.exists()
        ? { ...FALLBACK_TIMECARD_CONFIG, ...cfgSnap.data() }
        : FALLBACK_TIMECARD_CONFIG;
      setTimecardConfig(cfg);

      const paySnap = await getDoc(doc(db, "appConfig", "payRates"));
      const payConfig = paySnap.exists() ? paySnap.data() : {};

      const position = userData?.position || "";
    const base = payConfig?.positions?.[position];
let payRate = readMoney(base); 

if (payRate === null && String(position).trim().toLowerCase() === "paramedic") {
  payRate = 39;
}

      if (userData?.role === "Supervisor" && typeof payRate === "number") {
        payRate += Number(payConfig?.supervisorBump || 0);
      }

      const providerLevel = userData?.payTitle || position || "—";

      const qy = query(
        punchEntriesRef(uid),
        where("startTime", ">=", weekStart),
        where("startTime", "<=", weekEnd),
        orderBy("startTime", "asc"),
        limit(500)
      );

      const snap = await getDocs(qy);
      const entries = snap.docs.map((d) => d.data());

const builtRows = buildRows(entries, payRate, providerLevel);

const printedPayRate =
  builtRows.find((r) => typeof r.payRate === "number")?.payRate ?? payRate;

      const totalHours = builtRows.reduce((s, r) => s + r.hours, 0);
      const totalPay = builtRows.reduce((s, r) => s + (r.pay || 0), 0);

      let supSig = "";
      if (cfg?.supervisorUid) {
        const supervisorSnap = await getDoc(doc(db, "users", cfg.supervisorUid));
        const supData = supervisorSnap.exists() ? supervisorSnap.data() : {};
        supSig = getSignatureUrl(supData);
      }
      setSupervisorSignature(supSig);

      const fullAddress = buildFullAddress(userData);

     setUser({
  ...userData,
  uid,
  displayName:
    userData?.displayName || auth.currentUser?.displayName || "User",
  providerLevel,
  payRate: printedPayRate,
  fullAddress,
  signatureURL: getSignatureUrl(userData),
});

      setRows(builtRows);
      setTotals({
        totalHours: Number(totalHours.toFixed(2)),
        totalPay: Number(totalPay.toFixed(2)),
      });
    }

    load();
  }, [uid, weekStart, weekEnd]);

function handlePrint() {
  document.body.classList.add("self-timecard-printing");

  window.print();

  setTimeout(() => {
    document.body.classList.remove("self-timecard-printing");
  }, 500);
}

  if (!uid) return <div style={{ padding: 20 }}>Not signed in</div>;

 return (
  <div style={{ padding: 20, overflowX: "auto" }}>
    <div className="no-print" style={{ marginBottom: 10 }}>
      <button onClick={() => setWeekStart(addDays(weekStart, -7))}>
        ◀ Prev
      </button>

      <button onClick={() => setWeekStart(startOfWeekMonday(new Date()))}>
        This Week
      </button>

      <button onClick={() => setWeekStart(addDays(weekStart, 7))}>
        Next ▶
      </button>
    </div>

    <div className="no-print" style={{ marginBottom: 12 }}>
      <button onClick={handlePrint}>Print / Save PDF</button>
    </div>

    <div
      id="self-timecard-print-area"
      ref={printRef}
      style={{
        width: "8.5in",
        minHeight: "11in",
        margin: "0 auto",
        background: "#fff",
      }}
    >
      {user && (
        <PrintableTimecard
          user={user}
          rows={rows}
          totals={totals}
          weekStart={weekStart}
          supervisorSignature={supervisorSignature}
          businessUnit={timecardConfig.businessUnit || "32001"}
          department={timecardConfig.department}
          accountNumber={timecardConfig.accountNumber}
          supervisorName={timecardConfig.supervisorName}
        />
      )}
    </div>
  </div>
);
}